//libs
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

//constants
import { AUTH_URI } from 'shared/constants/uri';
import { TEXTS } from './constants';
import { TAG_IDENTIFIER, ERROR_TAGS } from 'shared/helpers/newRelic/constants';
import { TEXTS as _REWARDS_TEXTS } from 'shared/components/FootlockerRewards/components/RewardsPage/constants';

//helpers
import { getMapStateToProps } from 'shared/components/MyAccount/components/CustomerInfo/helpers';
import { getRedirectionURLWithCurrentPage } from 'shared/components/OTPVerification/helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// hooks
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useKeyboardClick } from 'shared/hooks/useKeyboardClick';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

import {
  getCustomerMobileNumber,
  getCustomerEmail,
  isCustomerFromIndia,
  getCustomerFirstName,
  shouldHideCustomerEmail,
  shouldMergeAccount,
  isLoggedIn,
} from 'shared/store/app/selectors';
import { log } from 'shared/helpers/newRelic';
import {
  getImageFormat,
  getProfileData,
  getProfileImage,
} from 'shared/layouts/MyProfilePage/Mobile/helpers';
import isEmpty from 'lodash/isEmpty';
import { useHistory, useLocation } from 'react-router';
import { useAuthModalWrapperContext } from 'shared/components/AuthModal/AuthModalWrapperContext';

//components
import Loader from 'shared/components/Loader';

//styles
import {
  Notification,
  LoginData,
  LoginMessages,
  HorizontalLine,
  GoToProfile,
  LoginNameMsg,
  ProfileImage,
  GreetingMessages,
  StatusMessage,
  StatusContainer,
  TopSectionContainer,
  ImageSection,
  ProfileImageRewards,
  ProfileDetailsSection,
  FirstSection,
  ThirdSection,
  RewardsHeading,
  MemberSince,
  PointsSection,
  ViewHistory,
  HistoryButton,
  RewardPointHeading,
  Points,
  PointsDisplay,
  HeadingSection,
} from './styles';

//icons
import GuestProfile from 'shared/svg/GuestProfile';
import { LOGIN_LOCATIONS } from 'shared/helpers/analytics/constants/loginEvents';
import ArrowRight from '@nykaa/ui-components/Icons/arrow-right';

// types
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { ACCOUNT_EVENTS } from 'shared/helpers/analytics/constants/accountEvents';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { formatMemberSinceDate } from 'shared/components/FootlockerRewards/components/RewardsPage/helpers';

// api
import { fetchRewardsPageData } from 'shared/store/RewardsPage/services';
import { getLoggedInUser } from 'shared/api/auth';

const RIGHT_ARROW_ICON_SIZE = '24px';

// Aria Label and Alt Text Constants
const ARIA_LABELS = {
  GO_TO_PROFILE: 'Go to profile',
};

const ALT_TEXT = {
  PROFILE_IMAGE: 'Profile',
};

function CustomerInfo() {
  const email = useSelector(getCustomerEmail);
  const mobile = useSelector(getCustomerMobileNumber);
  const hideEmail = useSelector(shouldHideCustomerEmail);
  const isIndianUser = useSelector(isCustomerFromIndia);
  const allowedToMergeAccounts = useSelector(shouldMergeAccount);
  const userName = useSelector(getCustomerFirstName);
  const alreadyLoggedIn = useSelector(isLoggedIn);
  const [showLoader, setShowLoader] = useState(false);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const [imageData, setImageData] = useState('');
  const [imageType, setImageType] = useState('');
  const { showBottomSheetAuth } = useAuthModalWrapperContext();
  const theme: Theme = useTheme();
  const isRewardsPageEnabled = useRemoteConfig('rewardsPageEnabled') || false;
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch logged-in user data
        const custData = await getLoggedInUser();
        const customerId = custData?.response?.customerData?.id;

        if (!customerId) {
          throw new Error('Customer ID not found. Please log in again.');
        }
        // Fetch rewards page data
        const data = await fetchRewardsPageData({
          headers: {},
          customerId: customerId, // Pass customerId in params
        });

        setApiData(data?.data?.userDetailsDTO);
        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        setError(err.message || 'Failed to load data. Please try again later.');
      }
    };

    if (alreadyLoggedIn) {
      fetchData();
    }
  }, [alreadyLoggedIn]);

  const { shouldVerifyMobile, shouldVerifyEmail } = useSelector((state) =>
    getMapStateToProps({
      state,
      email,
      mobile,
      isIndianUser,
      allowedToMergeAccounts,
    })
  );

  const clickHandler = () => {
    setShowLoader(true);
  };

  const goToProfilePage = () => {
    NFA.track({
      type: TRACK_TYPE.EVENT,
      event: ACCOUNT_EVENTS.PROFILE_CLICKED,
    });
    if (!alreadyLoggedIn) {
      NFA.updateDataLayer({ loginLocation: LOGIN_LOCATIONS.MY_ACCOUNT_LOGIN_BTN });
      showBottomSheetAuth('', false);
      return;
    }
    history.push('/my-profile');
  };

  const profileKeyboardProps = useKeyboardClick(goToProfilePage);

  useEffect(() => {
    if (alreadyLoggedIn && !imageType) {
      getProfileData()
        .then((res) => {
          const imageUrl = res?.response?.user?.image_url;
          // get image id
          const imageId = new URL(imageUrl).searchParams.get('id');
          setImageType(imageId);

          return getProfileImage(imageId).then((image) => {
            setImageData(image);
          });
        })
        .catch((e) => {
          log({
            errTitle: 'Error in getProfileData',
            err: e,
            tags: { [TAG_IDENTIFIER.MODULE]: ERROR_TAGS.MY_PROFILE },
          });
        });
    }
  }, [alreadyLoggedIn, imageType]);

  const userData = useMemo(() => {
    if (alreadyLoggedIn) {
      return {
        userName: userName ? userName : TEXTS.GREETING_GUEST_NAME,
        status: alreadyLoggedIn,
        textBelowGreet: `${TEXTS.LOGGED_IN_MSG} ${isEmpty(mobile) === false ? mobile : !isEmpty(email) && !hideEmail ? email : ''
          }`,
      };
    }
    return {
      userName: TEXTS.GREETING_GUEST_NAME,
      status: alreadyLoggedIn,
      textBelowGreet: TEXTS.ASK_TO_LOGIN + ' >',
    };
  }, [alreadyLoggedIn, email, hideEmail, mobile, userName]);

  const imageFormat = getImageFormat(imageType);

  const notificationData = useMemo(() => {
    if (isEmpty(mobile) && shouldVerifyMobile) return { url: AUTH_URI.MOBILE_NUMBER_VERIFICATION };

    if (shouldVerifyEmail) return { url: AUTH_URI.MERGE_ACCOUNTS };
  }, [mobile, shouldVerifyEmail, shouldVerifyMobile]);

  const oldFlow = () => {
    return (
      <>
        <LoginMessages {...profileKeyboardProps} aria-label={ARIA_LABELS.GO_TO_PROFILE}>
          <GreetingMessages>
            <LoginNameMsg>{TEXTS.GREETING_TEXT}</LoginNameMsg>
            {!!userData?.userName?.length && (
              <LoginNameMsg cs-data-id="username" data-cs-mask>
                {userData.userName}
              </LoginNameMsg>
            )}
          </GreetingMessages>
          <GoToProfile>
            {imageData ? (
              <ProfileImage
                data-cs-mask
                alt={ALT_TEXT.PROFILE_IMAGE}
                src={`data:${imageFormat};charset=utf-8;base64,${imageData}`}
              />
            ) : (
              <GuestProfile />
            )}
          </GoToProfile>
        </LoginMessages>

        <StatusContainer>
          <StatusMessage
            {...(!alreadyLoggedIn ? profileKeyboardProps : {})}
            {...(!alreadyLoggedIn ? { 'aria-label': ARIA_LABELS.GO_TO_PROFILE } : {})}
            isLoggedIn={userData.status}
            {...(alreadyLoggedIn ? {} : { 'data-at': 'login-btn' })}
            data-cs-mask
          >
            {isRewardsPageEnabled ? 'Sign In | Join FLX' : userData.textBelowGreet}
          </StatusMessage>
          <HorizontalLine />
        </StatusContainer>
      </>
    );
  };

  return (
    <>
      {!userData.status ? (
        <LoginData>{oldFlow()}</LoginData>
      ) : (
        <LoginData>
          {isRewardsPageEnabled ? (
            <>
              <LoginMessages {...profileKeyboardProps} aria-label={ARIA_LABELS.GO_TO_PROFILE}>
                <TopSectionContainer>
                  <GreetingMessages className="greeting-msg">
                    <LoginNameMsg>{TEXTS.GREETING_TEXT}</LoginNameMsg>
                    {!!userData?.userName?.length && (
                      <LoginNameMsg>{userData.userName}</LoginNameMsg>
                    )}
                  </GreetingMessages>
                  <ImageSection>
                    {imageData && (
                      <ProfileImageRewards
                        alt={ALT_TEXT.PROFILE_IMAGE}
                        src={`data:${imageFormat};charset=utf-8;base64,${imageData}`}
                      />
                    )}
                  </ImageSection>
                </TopSectionContainer>
              </LoginMessages>

              <ProfileDetailsSection
                loading={loading}
                error={error?.length || isEmpty(apiData) ? true : false}
              >
                {loading ? (
                  <Loader />
                ) : (
                  <>
                    {!error && !isEmpty(apiData) && (
                      <FirstSection>
                        <HeadingSection>
                          <RewardsHeading>{_REWARDS_TEXTS.FLX_REWARDS_HEADING}</RewardsHeading>
                          <MemberSince>
                            Member Since{' '}
                            {formatMemberSinceDate(apiData?.rewardDetails?.memberSinceDate)}
                          </MemberSince>
                        </HeadingSection>
                        <PointsSection className="first-section">
                          <RewardPointHeading>{_REWARDS_TEXTS.REWARD_POINTS}</RewardPointHeading>
                          <PointsDisplay className="first-section-points">
                            <Points>{apiData?.rewardDetails?.rewardPoints}</Points>
                          </PointsDisplay>
                        </PointsSection>
                      </FirstSection>
                    )}

                    <ThirdSection
                      onClick={
                        isEmpty(apiData)
                          ? () =>
                            window.open('https://www.footlocker.co.in/cp/flx-rewards', '_self')
                          : () => history.push('/flx-summary')
                      }
                    >
                      <ViewHistory>
                        {isEmpty(apiData)
                          ? _REWARDS_TEXTS.FLX_REWARDS_DASBOARD_NO_DATA
                          : _REWARDS_TEXTS.FLX_REWARDS_DASHBOARD}{' '}
                      </ViewHistory>
                      <HistoryButton>
                        <ArrowRight color={theme.colors.primary} size={RIGHT_ARROW_ICON_SIZE} />
                      </HistoryButton>
                    </ThirdSection>
                  </>
                )}
              </ProfileDetailsSection>
            </>
          ) : (
            oldFlow()
          )}
        </LoginData>
      )}

      <Notification
        data-test="test-not"
        to={getRedirectionURLWithCurrentPage({
          url: notificationData?.url,
          search,
          pathname,
        })}
        onClick={clickHandler}
      ></Notification>
      {showLoader && <Loader fixed />}
    </>
  );
}

export default memo(CustomerInfo);
